/**
 * Using an Empyreal Crystal: a short channel, then its effect.
 *
 * Pressing and holding a crystal's action-bar hotkey starts the channel on the
 * host; the character plays the use animation while it runs. Holding through
 * it consumes the crystal and applies its effect at the end. Releasing early,
 * moving, attacking, dodging, interacting or falling cancels it, and the
 * crystal is kept. The channel is the stray-press guard: a tap does nothing.
 * See docs/empyreal-crystal-mechanics.md.
 */

import type { EmpyrealCrystalSize } from './types/crystals';

/**
 * How long a crystal use channels before it takes effect. Matches the use
 * animation (Quaternius UAL2 `Consume`, about 1.33s) so the effect lands as
 * the motion finishes.
 */
export const CRYSTAL_USE_CHANNEL_MS = 1300 as const;

/** Why a crystal use ended without taking effect. */
export type CrystalUseCancelReason =
    | 'released'
    | 'moved'
    | 'acted'
    | 'fell'
    | 'left'
    | 'run-ended'
    | 'crystal-gone';

/**
 * How long a crystal's timed effects last, by size (see the tier table in
 * docs/gameplay/01-Essence-and-Crystals.md). Using the same recipe again
 * while its effect is active refreshes it to the full duration; it doesn't
 * stack.
 */
export const CRYSTAL_EFFECT_DURATION_MS: Readonly<Record<EmpyrealCrystalSize, number>> = {
    splinter: 20_000,
    fragment: 60_000,
    complete: 180_000,
};

/**
 * Share of the user's max essence pool a Resonance crystal restores, by size.
 * Resonance is the basic, most common heal: no tradeoff, and less than the
 * costlier Sorrow Vitality crystals. First-pass values.
 */
export const RESONANCE_RESTORE_FRACTION: Readonly<Record<EmpyrealCrystalSize, number>> = {
    splinter: 0.15,
    fragment: 0.3,
    complete: 0.5,
};
